import React from "react";
import { Link } from "@/i18n/router";
import { motion } from "framer-motion";
import { Check, Minus, Info, AlertTriangle, CheckCircle2, ArrowRight, ArrowDown } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";

const TONE = {
  info: { icon: Info, cls: "border-astro-blue/40 bg-astro-blue/10", ic: "text-astro-light" },
  warn: { icon: AlertTriangle, cls: "border-amber-500/40 bg-amber-500/10", ic: "text-amber-300" },
  success: { icon: CheckCircle2, cls: "border-emerald-500/40 bg-emerald-500/10", ic: "text-emerald-300" },
};

export function DocContent({ blocks }) {
  return (
    <div className="space-y-8">
      {blocks.map((b, i) => <Block key={i} b={b} />)}
    </div>
  );
}

function Block({ b }) {
  switch (b.type) {
    case "p":
      return <p className="text-[15px] leading-relaxed text-silver/75">{b.text}</p>;
    case "h":
      return <h2 id={slugify(b.text)} className="scroll-mt-28 pt-4 font-heading text-2xl font-bold text-white">{b.text}</h2>;
    case "list":
      return (
        <ul className="space-y-2.5">
          {b.items.map((it, i) => (
            <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-silver/75">
              <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-astro-light" /> {it}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="space-y-2.5">
          {b.items.map((it, i) => (
            <li key={i} className="flex items-start gap-3 text-[15px] leading-relaxed text-silver/75">
              <span className="font-heading font-bold text-astro-light">{i + 1}.</span> {it}
            </li>
          ))}
        </ol>
      );
    case "code":
      return (
        <figure className="overflow-hidden rounded-xl border border-navy-border bg-[#0b1120]">
          {b.caption && <figcaption className="flex items-center justify-between border-b border-navy-border px-4 py-2.5 text-xs font-medium text-silver/50"><span>{b.caption}</span><span className="uppercase tracking-wider text-astro-light/70">{b.lang}</span></figcaption>}
          <pre className="overflow-x-auto p-4 text-[13px] leading-relaxed"><code className="font-mono text-silver/85">{b.code}</code></pre>
        </figure>
      );
    case "table":
      return (
        <Reveal>
          <div className="overflow-x-auto rounded-xl border border-navy-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-navy-surface">
                <tr className="border-b border-navy-border">
                  {b.headers.map((h, i) => (
                    <th key={i} className={`px-4 py-3 font-heading font-semibold ${i === 0 ? "text-white" : "text-astro-light"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r, ri) => (
                  <tr key={ri} className="border-b border-navy-border/50 bg-navy last:border-0">
                    {r.map((c, ci) => (
                      <td key={ci} className={`px-4 py-3 ${ci === 0 ? "font-medium text-white" : "text-silver/75"}`}>
                        {c === true ? <Check className="h-4 w-4 text-emerald-400" /> : c === false ? <Minus className="h-4 w-4 text-silver/30" /> : c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      );
    case "callout": {
      const t = TONE[b.tone] || TONE.info;
      const Icon = t.icon;
      return (
        <div className={`flex gap-3 rounded-xl border p-4 ${t.cls}`}>
          <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${t.ic}`} />
          <div>
            {b.title && <p className="font-heading text-sm font-semibold text-white">{b.title}</p>}
            <p className="mt-0.5 text-sm leading-relaxed text-silver/75">{b.text}</p>
          </div>
        </div>
      );
    }
    case "steps":
      return (
        <div className="space-y-3">
          {b.items.map((it, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <div className="flex items-start gap-4 rounded-xl border border-navy-border bg-navy-surface p-5">
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-astro-blue/15 font-heading text-sm font-bold text-astro-light">{i + 1}</span>
                <div>
                  <h3 className="font-heading font-semibold text-white">{it.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-silver/65">{it.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      );
    case "image":
      return (
        <Reveal>
          <figure className="overflow-hidden rounded-2xl border border-navy-border bg-navy-surface">
            <img src={b.src} alt={b.alt} loading="lazy" className="w-full" />
            {b.caption && <figcaption className="border-t border-navy-border px-4 py-3 text-center text-xs text-silver/50">{b.caption}</figcaption>}
          </figure>
        </Reveal>
      );
    case "cards":
      return (
        <div className="grid gap-4 sm:grid-cols-2">
          {b.items.map((it, i) => {
            const inner = (
              <>
                <h3 className="font-heading font-semibold text-white group-hover:text-astro-light transition-colors">{it.title}</h3>
                <p className="mt-1.5 flex-1 text-sm leading-relaxed text-silver/65">{it.text}</p>
                {it.to && <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-astro-light">Read <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>}
              </>
            );
            const cls = "group flex h-full flex-col rounded-xl border border-navy-border bg-navy-surface p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-astro-blue/40";
            return it.to
              ? <Link key={i} to={it.to} className={cls}>{inner}</Link>
              : <div key={i} className={cls}>{inner}</div>;
          })}
        </div>
      );
    case "diagram":
      return <LayerDiagram title={b.title} layers={b.layers} />;
    default:
      return null;
  }
}

function LayerDiagram({ title, layers }) {
  return (
    <Reveal>
      <div className="rounded-2xl border border-navy-border bg-[#0b1120] p-6">
        {title && <p className="mb-5 text-center font-heading text-sm font-semibold uppercase tracking-wider text-astro-light">{title}</p>}
        <div className="space-y-2">
          {layers.map((layer, li) => (
            <React.Fragment key={li}>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: li * 0.08 }}
                className="group rounded-xl border border-navy-border bg-navy-surface p-4 transition-colors hover:border-astro-blue/50"
              >
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-silver/50 group-hover:text-astro-light transition-colors">{layer.name}</p>
                <div className="flex flex-wrap gap-2">
                  {layer.nodes.map((n) => (
                    <span key={n} className="rounded-lg border border-navy-border bg-navy px-3 py-2 text-sm font-medium text-silver/85 transition-colors group-hover:border-astro-blue/30">{n}</span>
                  ))}
                </div>
              </motion.div>
              {li < layers.length - 1 && (
                <div className="flex justify-center py-0.5"><ArrowDown className="h-4 w-4 text-astro-light/50" /></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </Reveal>
  );
}

export const slugify = (s = "") => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
